import { Resources } from '@ragu-components/ord-resources';
import { PermissionFields } from '@ragu-components/ord-types';

import { flowchartLogger } from 'src/lib/logger';

import type { ResourceCallbacks, RaRecord, Identifier } from 'react-admin';

// Temporary in-memory key used to pass selected inbound numbers
// from beforeCreate to afterSave without sending them in the HTTP payload.
const INBOUND_E164_IDS_SYMBOL = Symbol('inbound_e164_ids');

type CallflowCallbackRecord = RaRecord & {
  inbound_e164_ids?: Identifier[];
  [INBOUND_E164_IDS_SYMBOL]?: Identifier[];
};

export const callflowCallbacks: ResourceCallbacks = {
  resource: PermissionFields.callflows,
  beforeCreate: async (params) => {
    if ('inbound_e164_ids' in params.data) {
      const callflowData = params.data as CallflowCallbackRecord;
      const inboundE164Ids = callflowData.inbound_e164_ids;
      delete params.data.inbound_e164_ids;
      if (inboundE164Ids !== undefined) {
        (params.data as CallflowCallbackRecord)[INBOUND_E164_IDS_SYMBOL] = inboundE164Ids;
      }
    }
    return params;
  },
  afterSave: async (data, dataProvider) => {
    const callflowData = data as CallflowCallbackRecord;
    const inboundE164Ids = callflowData[INBOUND_E164_IDS_SYMBOL];
    callflowData[INBOUND_E164_IDS_SYMBOL] = undefined;

    if (inboundE164Ids !== undefined) {
      await Promise.all(
        inboundE164Ids.map(async (inboundE164Id) => {
          await dataProvider.putAssociation(
            PermissionFields.callflows,
            callflowData.id,
            'assigned_inbound_e164s',
            inboundE164Id
          );
        })
      );
    }
    return data;
  },
  beforeUpdate: async (params, dataProvider) => {
    if ('assigned_inbound_e164s' in params.data) {
      delete params.data.assigned_inbound_e164s;
    }
    if ('inbound_e164_ids' in params.data) {
      const inboundE164Ids: Identifier[] = params.data.inbound_e164_ids;
      delete params.data.inbound_e164_ids;
      if (inboundE164Ids !== undefined) {
        try {
          // Get previously assigned inbound_e164s
          const { data } = await dataProvider.getList('assigned_inbound_e164s', {
            filter: {},
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'e164_match', order: 'ASC' },
            meta: {
              parentResource: Resources.callflows,
              parentId: params.id,
            },
          });
          const assignedInboundE164Ids = data.map((inboundE164: RaRecord) => inboundE164.id);

          for (const inboundE164Id of inboundE164Ids) {
            // Only associate new inbound_e164s
            if (!assignedInboundE164Ids.includes(inboundE164Id)) {
              await dataProvider.putAssociation(
                PermissionFields.callflows,
                params.id,
                'assigned_inbound_e164s',
                inboundE164Id
              );
            } else {
              // if an element was assigned previously, but not selected anymore, remove it
              assignedInboundE164Ids.splice(assignedInboundE164Ids.indexOf(inboundE164Id), 1);
            }
          }

          // Remove association from all inbound_e164s that were removed from selection
          for (const inboundE164Id of assignedInboundE164Ids) {
            await dataProvider.delAssociation(
              PermissionFields.callflows,
              params.id,
              'assigned_inbound_e164s',
              inboundE164Id
            );
          }
        } catch (error) {
          flowchartLogger.error('Failed to fetch inbound_e164s', { error });
        }
      }
    }
    return params;
  },
};
